import { useEffect } from 'react';
import { BrowserRouter, BrowserRouterProps } from 'react-router-dom';

// Store params in memory (outside React to persist across renders)
const routeParams = new Map<string, string>();

export function clearSavedRouteParams(pathname: string) {
  routeParams.delete(pathname);
}

interface EnhancedRouterProps extends BrowserRouterProps {
  basename?: string;
}

export function EnhancedRouter({
  basename = import.meta.env.BASE_URL,
  ...props
}: EnhancedRouterProps) {
  useEffect(() => {
    // Save initial params if they exist
    const initialSearchParams = new URLSearchParams(window.location.search);
    if (initialSearchParams.toString()) {
      routeParams.set(window.location.pathname, initialSearchParams.toString());
    }

    // Enhance the router's history
    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(
      window.history
    );

    const enhanceUrl = (url: string | URL) => {
      const urlObj = new URL(url, window.location.origin);
      if (!urlObj.search) {
        const savedParams = routeParams.get(urlObj.pathname);
        if (savedParams) {
          urlObj.search = `?${savedParams}`;
        }
      } else {
        // Update saved params when new ones are provided
        routeParams.set(urlObj.pathname, urlObj.searchParams.toString());
      }
      return urlObj;
    };

    window.history.pushState = (
      data: any,
      unused: string,
      url: string | URL
    ) => {
      const urlObj = enhanceUrl(url);
      originalPushState(data, unused, urlObj);
    };

    window.history.replaceState = (
      data: any,
      unused: string,
      url: string | URL
    ) => {
      const urlObj = enhanceUrl(url);
      originalReplaceState(data, unused, urlObj);
    };

    // Save params on navigation
    const handleNavigation = () => {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.toString()) {
        routeParams.set(window.location.pathname, searchParams.toString());
      }
    };

    // Handle lazy loaded routes by also listening for route changes
    const handleRouteChange = () => {
      setTimeout(handleNavigation, 0);
    };

    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('routechange', handleRouteChange);

    // Initial load with delay to handle lazy loading
    setTimeout(handleNavigation, 0);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('routechange', handleRouteChange);
    };
  }, []);

  return <BrowserRouter basename={basename} {...props} />;
}
